import noop from 'lodash-es/noop';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import ActionButton from '@formstack/phoenix/components/Button/ActionButton';
import { ActionVariants } from '@formstack/phoenix/components/actionVariants/constants';
import { Colors as PHXColors } from '@formstack/phoenix/components/colors/constants';
import { ElementSizeVariants } from '@formstack/phoenix/components/componentSizeVariants/constants';
import { IconName } from '@formstack/phoenix/components/icons/constants';

import { LoadingBoundary } from '@/common/components/LoadingBoundary';
import { ConnectorIds } from '@/common/constants';
import { useResource } from '@/common/resources';
import { useDeleteUserConnectorMutation } from '@/store/user-connectors';

import { DisconnectAppDialog } from './components/DisconnectAppDialog';
import { IConnectedAppItemActions } from './interface';

const { ['phx-cerulean']: PHX_CERULEAN } = PHXColors;

const ConnectButton = styled(ActionButton)`
  &.phx-Button {
    color: ${PHX_CERULEAN};
  }
`;

export const ConnectedAppItemActions = ({
  userConnectorId,
  connectorId,
  authType,
  editApp = noop,
  isConnected = false,
}: IConnectedAppItemActions) => {
  const resource = useResource((resource) => ({
    connect: resource.connect,
    disconnect: resource.disconnect,
  }));

  const [disconnectApp, { isLoading: isDisconnecting }] = useDeleteUserConnectorMutation();

  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const onEditApp = useCallback(() => {
    if (connectorId) {
      editApp(connectorId, authType);
    }
  }, [authType, connectorId, editApp]);

  const onDisconnectApp = useCallback(() => {
    disconnectApp(userConnectorId as ConnectorIds);
  }, [disconnectApp, userConnectorId]);

  const onConfirmDisconnect = useCallback(() => {
    setConfirmingDisconnect(true);
  }, [setConfirmingDisconnect]);

  const onCancelDisconnect = useCallback(() => {
    setConfirmingDisconnect(false);
  }, [setConfirmingDisconnect]);

  const onDisconnectConfirmed = useCallback(() => {
    setConfirmingDisconnect(false);
    onDisconnectApp();
  }, [onDisconnectApp, setConfirmingDisconnect]);

  return (
    <LoadingBoundary isLoading={isDisconnecting}>
      {isConnected ? (
        <>
          <ActionButton
            dataTest={`disconnect-${connectorId}`}
            icon={IconName.logout}
            noBackground={true}
            action={onConfirmDisconnect}
            buttonSize={ElementSizeVariants.small}
            variant={ActionVariants.deleteVariant}
            buttonText={resource.disconnect}
          />
          <DisconnectAppDialog
            open={confirmingDisconnect}
            onConfirm={onDisconnectConfirmed}
            onCancel={onCancelDisconnect}
          />
        </>
      ) : (
        <>
          <ConnectButton
            dataTest={`connect-${connectorId}`}
            icon={IconName['edit-circle']}
            noBackground={true}
            action={onEditApp}
            buttonSize={ElementSizeVariants.small}
            variant={ActionVariants.create}
            buttonText={resource.connect}
          />
        </>
      )}
    </LoadingBoundary>
  );
};
